import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { ActivityBuilderModal } from 'approot/activity-builder/activity-builder-modal';
import { FileDropperContextProvider } from 'lib/form/file-dropper/file-dropper.context';
import {
  SettingsGroup,
  SettingsLabel,
  SettingSmallText,
  SettingSmallTextEllipsis,
} from 'approot/activity-builder/settings/settings.style';
import { ACCEPT_TYPE_GIF } from 'lib/form/file-dropper/file-dropper.constants';
import {
  useAddGifFileToComponent,
  useFindComponentDimensions,
} from '../component.hooks';
import { SimpleModalVerticalActionsStyled } from 'lib/modal/simple-modal.styles';
import { PrimaryButton } from 'approot/shared/styles/buttons/primary-button';
import { CONTENT_IMAGE } from 'global/constants';
import { useImageRequestLoader } from 'approot/shared/images/images.hooks';
import TextInputLimit from 'approot/shared/text-input-limit/text-input-limit';
import { GENERATE_IMAGE_ALT_TEXT } from 'approot/dictionary/dictionary.graphql';
import type {
  GenerateImageAltTextQuery,
  GenerateImageAltTextQueryVariables,
} from '../../../dictionary/dictionary.graphql.generated';
import { useLoggingLazyQuery } from 'lib/apollo/apollo-hooks';
import { SecondaryLoadingButton } from 'approot/shared/styles/buttons/loading-button';
import { EditProps } from './gif.constants';
import ImageUploadField, { ImageProps } from '../shared/image-upload-field';
import { Box } from 'approot/shared/design-system/box/box.style';
import { PStyled } from 'approot/shared/styles/typography.style';
import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import ComponentImageSource from '../shared/component-image-modal/component-image-source';
import ComponentAltTextTooltip from '../shared/component-alt-text/component-alt-text-tooltip';
import { ALT_TEXT_MAX } from '../shared/component-alt-text/component-alt-text.constants';

const GIF_IMAGE_KEY = 'gifImageKey';
const PRINT_IMAGE_KEY = 'printImageKey';
const ALT_TEXT_FIELD = 'altText';

type FormData = {
  [GIF_IMAGE_KEY]?: string | null;
  [PRINT_IMAGE_KEY]?: string | null;
  [ALT_TEXT_FIELD]?: string | null;
};

type Props = EditProps & {
  fileToUpload?: {
    key: string;
    fileName: string;
    file: File;
  };
  onClose: () => void;
};

const GifModal = ({
  id,
  sectionId,
  properties,
  fileToUpload,
  onClose,
}: Props) => {
  const [gifData, setGifData] = useState<
    | (ImageProps & {
        file?: File;
      })
    | null
  >(fileToUpload ? fileToUpload : properties.image);

  const [printImageData, setPrintImageData] = useState<ImageProps | null>(
    properties.printImage
  );
  const [sourceUrl, setSourceUrl] = useState<string>(
    properties.sourceUrl || ''
  );

  const { elementDimensionLabel } = useFindComponentDimensions(id);
  const { contentId, contentType } = useGetInteractiveLessonParams();

  const { addGifFileToComponent, loading: loadingUpdate } =
    useAddGifFileToComponent({
      onCompleted: () => onClose(),
    });

  const [generateAltText, { loading: loadingAltText }] = useLoggingLazyQuery<
    GenerateImageAltTextQuery,
    GenerateImageAltTextQueryVariables
  >(GENERATE_IMAGE_ALT_TEXT, {
    fetchPolicy: 'no-cache',
    onCompleted: res => {
      const altText = res.generateImageAltText.altText.substring(0, 1000);
      setValue(ALT_TEXT_FIELD, altText);
      const altTextElement = document.getElementById(ALT_TEXT_FIELD);
      altTextElement?.focus();
    },
  });

  const formMethods = useForm<FormData>({
    defaultValues: {
      [GIF_IMAGE_KEY]: gifData?.key,
      [PRINT_IMAGE_KEY]: properties.printImage?.key,
      [ALT_TEXT_FIELD]: properties.image?.alt,
    },
  });
  const {
    formState: { errors },
    register,
    handleSubmit,
    setValue,
  } = formMethods;

  const { getImageResponsive } = useImageRequestLoader({
    url: properties.image?.original,
    onLoad: () => false,
  });

  const {
    imageUrl: printImageUrl,
    getImageResponsive: getPrintImageResponsive,
    setImageUrl: setPrintImageUrl,
  } = useImageRequestLoader({
    url: properties.printImage?.original,
    onLoad: () => false,
  });

  const handleGifFileChange = useCallback(
    (key: string, fileName: string) => {
      getImageResponsive({
        variables: {
          input: {
            key: key,
            imageType: CONTENT_IMAGE,
          },
        },
      }).then(res => {
        setGifData({
          ...gifData,
          key,
          fileName,
          original: res.data?.getImageResponsive.original,
        });
      });

      setValue(GIF_IMAGE_KEY, key, { shouldValidate: true });
    },
    [gifData, setGifData, setValue, getImageResponsive]
  );

  const handlePrintFileChange = useCallback(
    (key: string, fileName: string) => {
      getPrintImageResponsive({
        variables: {
          input: {
            key: key,
            imageType: CONTENT_IMAGE,
          },
        },
      }).then(res => {
        setPrintImageData({
          ...printImageData,
          key,
          fileName,
          original: res.data?.getImageResponsive.original,
        });
      });
      setValue(PRINT_IMAGE_KEY, key, { shouldValidate: true });
    },
    [printImageData, setPrintImageData, setValue, getPrintImageResponsive]
  );

  const handleDeleteGif = useCallback(() => {
    setGifData(null);
    setValue(GIF_IMAGE_KEY, undefined);
  }, [setGifData, setValue]);

  const handleDeletePrintImage = useCallback(() => {
    setPrintImageData(null);
    setValue(PRINT_IMAGE_KEY, undefined);
    setPrintImageUrl(undefined);
  }, [setPrintImageUrl, setValue]);

  const onSubmit = useCallback(
    (data: FormData) => {
      if (!gifData || !printImageData) return;

      addGifFileToComponent({
        variables: {
          input: {
            componentId: id,
            contentId,
            contentType,
            sectionId,
            sourceUrl,
            imageInput: {
              key: gifData.key,
              imageType: CONTENT_IMAGE,
              fileName: gifData.fileName,
              alt: data[ALT_TEXT_FIELD],
            },
            printImage: {
              key: printImageData.key,
              imageType: CONTENT_IMAGE,
              fileName: printImageData.fileName,
              alt: data[ALT_TEXT_FIELD],
            },
          },
        },
      });
    },
    [
      id,
      sectionId,
      contentId,
      contentType,
      gifData,
      printImageData,
      sourceUrl,
      addGifFileToComponent,
    ]
  );

  useEffect(() => {
    register(GIF_IMAGE_KEY, {
      required: 'Oops! It looks like you haven’t chosen a GIF File.',
    });
    register(PRINT_IMAGE_KEY, {
      required: 'Oops! It looks like you haven’t set an image for print.',
    });
  }, [register]);

  return (
    <ActivityBuilderModal
      isVisible
      title="Animated GIF"
      headerTitle="Animated GIF"
      size={500}
      onClose={onClose}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <SettingsGroup>
            <Box $justifyContent="space-between" $marginBottom={0}>
              <SettingsLabel>GIF File</SettingsLabel>
              {gifData?.key && (
                <SettingSmallText marginBottom={0}>
                  {elementDimensionLabel}
                </SettingSmallText>
              )}
            </Box>
            <ImageUploadField
              fieldName={GIF_IMAGE_KEY}
              accept={[ACCEPT_TYPE_GIF]}
              showAcceptExtensions={false}
              image={gifData ? gifData : null}
              elementDimensionLabel={elementDimensionLabel}
              error={errors[GIF_IMAGE_KEY]?.message}
              onImageUpload={handleGifFileChange}
              onDelete={handleDeleteGif}
            />
            {gifData?.key && (
              <Box $marginTop={4}>
                <SettingSmallTextEllipsis>
                  {gifData?.fileName}
                </SettingSmallTextEllipsis>
              </Box>
            )}
          </SettingsGroup>
          <SettingsGroup>
            <Box $justifyContent="space-between" $marginBottom={0}>
              <SettingsLabel>Static Image for Print</SettingsLabel>
              {printImageData?.key && (
                <SettingSmallText marginBottom={0}>
                  {elementDimensionLabel}
                </SettingSmallText>
              )}
            </Box>
            <ImageUploadField
              fieldName={PRINT_IMAGE_KEY}
              image={printImageData ? printImageData : null}
              elementDimensionLabel={elementDimensionLabel}
              error={errors[PRINT_IMAGE_KEY]?.message}
              onImageUpload={handlePrintFileChange}
              onDelete={handleDeletePrintImage}
            />
            {printImageData?.key && (
              <Box $marginTop={4}>
                <SettingSmallTextEllipsis>
                  {printImageData?.fileName}
                </SettingSmallTextEllipsis>
              </Box>
            )}
          </SettingsGroup>
          <ComponentImageSource sourceUrl={sourceUrl} onChange={setSourceUrl} />
          <SettingsGroup marginBottom={0}>
            <Box $justifyContent="space-between">
              <SettingsLabel htmlFor={ALT_TEXT_FIELD}>
                Alt Text
                <ComponentAltTextTooltip />
              </SettingsLabel>
              <PStyled marginBottom={0}>Optional</PStyled>
            </Box>

            <TextInputLimit
              rows={3}
              maxLength={ALT_TEXT_MAX}
              id={ALT_TEXT_FIELD}
              name={ALT_TEXT_FIELD}
              placeholder={
                loadingAltText
                  ? 'Generating Alt Text...'
                  : 'Describe this image'
              }
              defaultValue={gifData?.alt || ''}
              isDisabled={loadingAltText || loadingUpdate}
            />
          </SettingsGroup>
          <SimpleModalVerticalActionsStyled>
            <SecondaryLoadingButton
              data-selector="generate-alt-text"
              isLoading={loadingAltText}
              isDisabled={loadingAltText || loadingUpdate || !printImageUrl}
              aria-disabled={loadingAltText || loadingUpdate || !printImageUrl}
              onClick={() =>
                generateAltText({
                  variables: {
                    input: {
                      imageUrl: printImageUrl || '',
                    },
                  },
                })
              }
            >
              {loadingAltText ? 'Generating Alt Text' : 'Generate Alt Text ✨'}
            </SecondaryLoadingButton>
            <PrimaryButton
              type="submit"
              $isDisabled={loadingUpdate}
              loading={loadingUpdate}
            >
              Save
            </PrimaryButton>
          </SimpleModalVerticalActionsStyled>
        </form>
      </FormProvider>
    </ActivityBuilderModal>
  );
};

const GifEditModal = (props: Props) => (
  <FileDropperContextProvider>
    <GifModal {...props} />
  </FileDropperContextProvider>
);

export default GifEditModal;
